import React from 'react';
import { motion } from 'framer-motion';
import { Container } from '../ui/Container';
import { Microscope, Brain, Zap, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';

const TechnicalInfrastructureSection: React.FC = () => {
  return (
    <section className="py-32 bg-theme-bg relative overflow-hidden">
      <Container>
        <div className="text-center mb-24">
          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-blue-100/10 border border-blue-500/20 text-blue-500 text-xs font-bold uppercase tracking-widest mb-6">
            <span>The Bio-Loop</span>
          </div>
          <h2 className="text-5xl font-black text-theme-primary mb-6">Continuous Biological <br /> <span className="text-blue-500">Feedback Infrastructure.</span></h2>
          <p className="text-xl text-theme-secondary max-w-2xl mx-auto font-light leading-relaxed">
            Unlike annual snapshots, BIOTUNE creates a perpetual data cycle where every result drives an action, and every action is verified by the next result.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 relative">
          {/* Connecting Line (Desktop) */}
          <div className="hidden lg:block absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-blue-500/20 to-transparent -translate-y-1/2 -z-10" />

          {/* Step 1: Lab OS */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="flex-1 flex flex-col items-center text-center group"
          >
            <Link to="/ai-lab" className="flex flex-col items-center group">
              <div className="w-24 h-24 rounded-[32px] bg-blue-600/20 border border-blue-500/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-2xl shadow-blue-500/10">
                <Microscope className="w-10 h-10 text-blue-500" />
              </div>
              <h4 className="text-xl font-bold text-theme-primary mb-2">1. Quantify (Lab OS)</h4>
              <p className="text-sm text-theme-secondary max-w-[200px]">Monthly clinical panels capture your raw biological state.</p>
            </Link>
          </motion.div>

          <Arrow />

          {/* Step 2: AI OS */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="flex-1 flex flex-col items-center text-center group"
          >
            <Link to="/ai-lab" className="flex flex-col items-center group">
              <div className="w-24 h-24 rounded-[32px] bg-purple-600/20 border border-purple-500/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-2xl shadow-purple-500/10">
                <Brain className="w-10 h-10 text-purple-500" />
              </div>
              <h4 className="text-xl font-bold text-theme-primary mb-2">2. Analyze (AI OS)</h4>
              <p className="text-sm text-theme-secondary max-w-[200px]">Proprietary models translate data into actionable scores.</p>
            </Link>
          </motion.div>

          <Arrow />

          {/* Step 3: Action Pillars */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="flex-1 flex flex-col items-center text-center group"
          >
            <div className="w-24 h-24 rounded-[32px] bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-2xl shadow-emerald-500/10">
              <Zap className="w-10 h-10 text-emerald-500" />
            </div>
            <h4 className="text-xl font-bold text-theme-primary mb-2">3. Optimize (Action)</h4>
            <p className="text-sm text-theme-secondary max-w-[200px]">Bio-synced Food, Movement, and Supplements protocols.</p>
          </motion.div>

          <Arrow />

          {/* Step 4: Verification */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            className="flex-1 flex flex-col items-center text-center group"
          >
            <div className="w-24 h-24 rounded-[32px] bg-blue-600/20 border border-blue-500/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-2xl shadow-blue-500/10">
              <RefreshCw className="w-10 h-10 text-blue-500" />
            </div>
            <h4 className="text-xl font-bold text-theme-primary mb-2">4. Verify (Feedback)</h4>
            <p className="text-sm text-theme-secondary max-w-[200px]">Next month's lab results verify and tune the cycle.</p>
          </motion.div>
        </div>
      </Container>
    </section>
  );
};

const Arrow = () => (
    <div className="hidden lg:flex items-center text-theme-border/50">
        <motion.div
            animate={{ x: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
        >
            <Zap className="w-4 h-4" />
        </motion.div>
    </div>
)

export default TechnicalInfrastructureSection;
